import {
  AndroidDeliveryPriority,
  IosBadgeCountMode,
  IosInterruptionLevel,
  type IosBadgeConfig,
} from "./config-types";
import type { RcsContent } from "./rcs-content";

export type PushClickBehavior = "open_app" | "deep_link" | "web_url";

// Push Advanced Config types (maps to push_config table)
export type CustomDataEntry = { id: string; key: string; value: string };

export type PushAdvancedConfig = {
  // Cross-platform
  sound: string;
  messageExpiryHours: number;
  collapseKey: string;
  customData: CustomDataEntry[];

  // iOS
  iosBadgeConfig: IosBadgeConfig;
  iosInterruptionLevel: IosInterruptionLevel;
  iosRelevanceScore: number;
  iosThreadId: string;

  // Android
  androidNotificationChannel: string;
  androidDeliveryPriority: AndroidDeliveryPriority;
  androidGroupKey: string;
  androidReplaceNotificationId: string;
};

export const DEFAULT_PUSH_ADVANCED_CONFIG: PushAdvancedConfig = {
  sound: "",
  messageExpiryHours: 72,
  collapseKey: "",
  customData: [],
  iosBadgeConfig: { mode: IosBadgeCountMode.NoChange },
  iosInterruptionLevel: IosInterruptionLevel.Active,
  iosRelevanceScore: 0,
  iosThreadId: "",
  androidNotificationChannel: "",
  androidDeliveryPriority: AndroidDeliveryPriority.Normal,
  androidGroupKey: "",
  androidReplaceNotificationId: "",
};

/** Form-level action button entry (platform is implicit from which array it belongs to). */
export type ActionButtonEntry = {
  id: string;
  name: string;
  url: string;
};

export type PushImage =
  | { type: "upload"; url: string }
  | { type: "url"; url: string }
  | null;

export type SmsEditorContent = {
  body: string;
  link?: string;
  mediaUrl?: string;
  /**
   * Which lane this message was authored in, written on save.
   * The card lane is opt-in at creation, so its absence means SMS-only
   */
  lane?: "rcs" | "fallback";
};

export type PushEditorContent = {
  body: string;
  image: PushImage;
  /**
   * Silent (background data) mode. When true the message displays no
   * notification — the SDK delivers `customData` to the host app and/or
   * updates the iOS badge. Display fields (title/body/image/buttons/etc.) are
   * meaningless in this mode. Top-level (not in advancedConfig) because
   * silentness is a whole-message decision, not an advanced tweak.
   */
  isSilent: boolean;
  sameClickBehaviorForBoth: boolean;
  iosClickBehavior: PushClickBehavior;
  iosClickUrl: string;
  androidClickBehavior: PushClickBehavior;
  androidClickUrl: string;
  sameActionButtonsForBoth: boolean;
  iosActionButtons: ActionButtonEntry[];
  androidActionButtons: ActionButtonEntry[];
  advancedConfig: PushAdvancedConfig;
};

/** The empty push editor state — the seed for a brand-new push A/B arm and the
 *  fallback when a saved arm carries no push editor_state. */
export const DEFAULT_PUSH_EDITOR_CONTENT: PushEditorContent = {
  body: "",
  image: null,
  isSilent: false,
  sameClickBehaviorForBoth: true,
  iosClickBehavior: "open_app",
  iosClickUrl: "",
  androidClickBehavior: "open_app",
  androidClickUrl: "",
  sameActionButtonsForBoth: true,
  iosActionButtons: [],
  androidActionButtons: [],
  advancedConfig: DEFAULT_PUSH_ADVANCED_CONFIG,
};

export type PushRenderedOutput = {
  title: string;
  body: string;
  subtitle?: string;
  imageUrl?: string | null;
};

export type SmsRenderedOutput = { body: string; rcs?: RcsContent };
